import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, TextField, Typography, useTheme } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../store/userContext';
import { ContactContext } from '../../store/contactContext';

type Props = {
    storage: StorageType;
};

const UsersOfStorage: React.FC<Props> = ({ storage }) => {
    const theme = useTheme();
    const { users, getUsers, getUsersLoading } = useContext(UserContext);
    const {
        contacts,
        getContacts,
        getContactsLoading,
        addContact,
        addContactLoading,
        deleteContact,
        deleteContactLoading,
    } = useContext(ContactContext);
    const { i18n } = useTranslation();
    const [isAddingEmail, setIsAddingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newEmailError, setNewEmailError] = useState('');

    useEffect(() => {
        getUsers();
    }, [getUsers]);

    const storageUsers = useMemo(() => {
        if (!storage.siteId) {
            return [];
        }

        return users.filter((user) => user.siteIds?.includes(storage.siteId as number));
    }, [users, storage.siteId]);

    // Report emails are sent to the ORDER contacts of the storage's site
    const notificationContacts = useMemo(
        () => contacts.filter((contact) => contact.siteId === storage.siteId && contact.contactType === 'ORDER'),
        [contacts, storage.siteId]
    );

    const closeNewEmail = () => {
        setIsAddingEmail(false);
        setNewEmail('');
        setNewEmailError('');
    };

    const handleAddEmail = () => {
        const email = newEmail.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setNewEmailError('Invalid email');
            return;
        }
        if (notificationContacts.some((contact) => contact.email.toLowerCase() === email.toLowerCase())) {
            setNewEmailError('Email already in the list');
            return;
        }
        addContact(
            {
                siteId: storage.siteId,
                contactType: 'ORDER',
                email,
                locale: i18n.language.toUpperCase() === 'FI' ? 'FI' : 'EN',
            },
            () => {
                closeNewEmail();
                getContacts(true);
            }
        );
    };

    const handleRemoveEmail = (contactId: number) => {
        deleteContact(contactId, () => getContacts(true));
    };

    return (
        <Grid size={{ xs: 12 }}>
            <Box
                sx={{
                    backgroundColor: theme.palette.headerBackground,
                    p: 2,
                    height: 50,
                    borderBottom: theme.palette.border,
                }}>
                <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
                    Users of storage
                </Typography>
            </Box>
            {getUsersLoading && (
                <Typography sx={{ mt: 1, ...theme.typography.usersFontRegular }}>
                    Loading users...
                </Typography>
            )}

            {!getUsersLoading && storageUsers.length === 0 && (
                <Typography sx={{ mt: 1, ...theme.typography.usersFontRegular }}>
                    No users for {storage.name}.
                </Typography>
            )}

            {!getUsersLoading && storageUsers.map((user) => (
                <Box
                    key={user.id}
                    sx={{
                        display: 'flex',
                        height: '30px',
                        minHeight: '30px',
                        px: 1.5,
                        alignItems: 'center',
                        gap: '8px',
                        ...theme.typography.usersFontTitle,
                        mb: 0.5,
                        borderRadius: '4px',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {user.email}
                    </Box>
                </Box>
            ))}
            <Box
                sx={{
                    backgroundColor: theme.palette.headerBackground,
                    px: 2,
                    height: 50,
                    borderBottom: theme.palette.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
                    Storage Notifications
                </Typography>
                <IconButton
                    aria-label="Add notification email"
                    size="small"
                    onClick={() => setIsAddingEmail(true)}
                    disabled={!storage.siteId}
                    sx={{ color: 'green' }}
                >
                    <AddCircleIcon />
                </IconButton>
            </Box>

            {isAddingEmail && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.5, mt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Email"
                        value={newEmail}
                        error={!!newEmailError}
                        helperText={newEmailError}
                        onChange={(e) => {
                            setNewEmail(e.target.value);
                            setNewEmailError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleAddEmail();
                            } else if (e.key === 'Escape') {
                                closeNewEmail();
                            }
                        }}
                        onBlur={() => {
                            if (!newEmail.trim()) {
                                closeNewEmail();
                            }
                        }}
                    />
                    <IconButton
                        aria-label="Confirm notification email"
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleAddEmail}
                        disabled={addContactLoading}
                        sx={{ color: 'green', mt: 0.5 }}
                    >
                        <AddCircleIcon />
                    </IconButton>
                </Box>
            )}

            {getContactsLoading && (
                <Typography sx={{ mt: 1, ...theme.typography.usersFontRegular }}>
                    Loading notification emails...
                </Typography>
            )}

            {!getContactsLoading && notificationContacts.length === 0 && !isAddingEmail && (
                <Typography sx={{ mt: 1, ...theme.typography.usersFontRegular }}>
                    No notification emails for {storage.name}.
                </Typography>
            )}

            {!getContactsLoading && notificationContacts.map((contact) => (
                <Box
                    key={contact.id}
                    sx={{
                        display: 'flex',
                        height: '30px',
                        minHeight: '30px',
                        px: 1.5,
                        alignItems: 'center',
                        gap: '8px',
                        ...theme.typography.usersFontTitle,
                        mb: 0.5,
                        borderRadius: '4px',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flexGrow: 1,
                        }}
                    >
                        {contact.email}
                    </Box>
                    <IconButton
                        aria-label={`Remove ${contact.email}`}
                        size="small"
                        onClick={() => handleRemoveEmail(contact.id)}
                        disabled={deleteContactLoading}
                        sx={{ color: 'grey.500' }}
                    >
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
        </Grid>
    );
};

export default UsersOfStorage;
